// PDP "You may also like" tabs - fills the two panes of
// sections/skullette-pdp-related.liquid from the Section Rendering API.
//   similar : /search?q=tag:design-x  -> minus anchor + set members
//             (empty -> the product's style collection, minus anchor)
//   recent  : /search?q=id:a OR id:b  -> Horizon's RecentlyViewed store,
//             minus the product on the page
// A tab with nothing to show stays hidden; both empty = the shelf hides.
import { RecentlyViewed } from '@theme/recently-viewed-products';

const SECTION_FILE = 'skullette-pdp-related';

class SkullettePdpRelated extends HTMLElement {
  connectedCallback() {
    if (this._booted) return;
    this._booted = true;
    this.max = parseInt(this.dataset.max, 10) || 8;
    this.exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    this.querySelectorAll('[data-rel-tab]').forEach((btn) =>
      btn.addEventListener('click', () => this.#show(btn.dataset.relTab))
    );
    // Defer the network until the shelf is near the viewport.
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      this.#load();
    }, { rootMargin: '600px 0px' });
    io.observe(this);
  }

  async #load() {
    const [similar, recent] = await Promise.all([this.#loadSimilar(), this.#loadRecent()]);
    const hasSimilar = this.#fill('similar', similar, this.exclude);
    const own = new Set([this.dataset.productId]);
    const hasRecent = this.#fill('recent', recent, own);
    this.#tab('recent').hidden = !hasRecent;
    this.#tab('similar').hidden = !hasSimilar;
    if (!hasSimilar && !hasRecent) {
      this.classList.add('is-empty');
      return;
    }
    this.#show(hasSimilar ? 'similar' : 'recent');
  }

  async #loadSimilar() {
    const tag = this.dataset.designTag;
    if (tag) {
      const items = await this.#fetchCards(this.#searchUrl(`tag:${tag}`));
      if (items.filter((el) => !this.exclude.has(el.dataset.pid)).length > 0) return items;
    }
    const styleUrl = this.dataset.styleUrl;
    if (!styleUrl) return [];
    const url = new URL(styleUrl, location.origin);
    url.searchParams.set('section_id', SECTION_FILE);
    return this.#fetchCards(url);
  }

  async #loadRecent() {
    const ids = RecentlyViewed.getProducts().filter((id) => id !== this.dataset.productId);
    if (ids.length === 0) return [];
    const items = await this.#fetchCards(this.#searchUrl(ids.map((id) => `id:${id}`).join(' OR ')));
    // search returns relevance order; restore most-recent-first
    const rank = new Map(ids.map((id, i) => [id, i]));
    return items.sort((a, b) => (rank.get(a.dataset.pid) ?? 99) - (rank.get(b.dataset.pid) ?? 99));
  }

  #searchUrl(q) {
    const url = new URL(this.dataset.searchUrl || '/search', location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', SECTION_FILE);
    return url;
  }

  async #fetchCards(url) {
    try {
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) return [];
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return [...doc.querySelectorAll('.sk-rel__item')];
    } catch {
      return [];
    }
  }

  #fill(name, items, exclude) {
    const grid = this.querySelector(`[data-rel-pane="${name}"] [data-rel-grid]`);
    if (!grid) return false;
    const seen = new Set();
    const keep = items.filter((el) => {
      const id = el.dataset.pid;
      if (!id || exclude.has(id) || seen.has(id)) return false;
      seen.add(id);
      return true;
    }).slice(0, this.max);
    grid.replaceChildren(...keep.map((el) => this.#plainPrice(document.importNode(el, true))));
    return keep.length > 0;
  }

  // Set / offer pages (data-hide-code) never print the store code, but the
  // lists render under /search where the code is live: collapse each card's
  // code price back to its plain list price.
  #plainPrice(node) {
    if (!this.hasAttribute('data-hide-code')) return node;
    node.querySelectorAll('.sk-price-with-code--code').forEach((el) => {
      const list = el.querySelector('.sk-price-with-code__was')?.textContent ?? '';
      el.classList.replace('sk-price-with-code--code', 'sk-price-with-code--plain');
      el.textContent = list;
    });
    return node;
  }

  #tab(name) {
    return this.querySelector(`[data-rel-tab="${name}"]`);
  }

  #show(name) {
    this.querySelectorAll('[data-rel-tab]').forEach((btn) => {
      const on = btn.dataset.relTab === name;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    this.querySelectorAll('[data-rel-pane]').forEach((pane) => {
      pane.hidden = pane.dataset.relPane !== name;
    });
  }
}

if (!customElements.get('skullette-pdp-related')) {
  customElements.define('skullette-pdp-related', SkullettePdpRelated);
}
